'use client'

import { useEffect, useState } from 'react'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import {
    getAttendantsByOrganization,
    deleteAttendant,
    toggleOnHold,
} from '@/lib/api/atendimento/attendants'
import { getDepartmentsByOrganization } from '@/lib/api/atendimento/departments'
import { Attendant } from '@/lib/api/common/interfaces/attendant.interface'
import { Department } from '@/lib/api/common/interfaces/department.interface'
import { toast } from 'react-toastify'
import {
    MdAdd,
    MdSearch,
    MdEdit,
    MdDelete,
    MdClose,
    MdBusiness,
    MdPause,
    MdPlayArrow,
    MdPsychology,
} from 'react-icons/md'
import { MdFileDownload } from 'react-icons/md'
import { HiSparkles } from 'react-icons/hi2'
import { BsRobot, BsCpu, BsLightning } from 'react-icons/bs'
import { useNavigate } from 'react-router-dom'
import ConfirmModal from '@/components/confirm-modal'
import { ImportDataModal } from '@/components/import/ImportDataModal'
import { ShareableModule } from '@/lib/api/share/share-api'

export default function AttendantsListPage() {
    const { user, isPartner } = useAuth()
    const navigate = useNavigate()
    const { canCreate, canUpdate, canDelete } = useResourcePermissions(
        'atendimento',
        'agents'
    )
    const [attendants, setAttendants] = useState<Attendant[]>([])
    const [departments, setDepartments] = useState<Department[]>([])
    const [filteredAttendants, setFilteredAttendants] = useState<Attendant[]>(
        []
    )
    const [displayedAttendants, setDisplayedAttendants] = useState<Attendant[]>(
        []
    )
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedDepartmentId, setSelectedDepartmentId] = useState<
        number | ''
    >('')
    const [itemsPerPage] = useState(15)
    const [currentPage, setCurrentPage] = useState(1)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [attendantToDelete, setAttendantToDelete] = useState<number | null>(
        null
    )
    const [deleting, setDeleting] = useState(false)
    const [showImportModal, setShowImportModal] = useState(false)

    // Stats
    const activeCount = attendants.filter(a => !a.onHold).length
    const pausedCount = attendants.filter(a => a.onHold).length

    useEffect(() => {
        if (!user) return
        const orgId = user?.organizationId ?? user?.company?.organizationId
        if (orgId) {
            loadAttendants()
            loadDepartments()
        } else {
            setLoading(false)
            toast.error(
                'Sua sessão não possui organização vinculada. Faça login novamente.'
            )
        }
    }, [user, user?.organizationId ?? user?.company?.organizationId])

    useEffect(() => {
        let filtered = attendants

        if (searchTerm.trim()) {
            filtered = filtered.filter(
                attendant =>
                    attendant.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase()) ||
                    attendant.rules
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
            )
        }

        if (selectedDepartmentId) {
            filtered = filtered.filter(
                attendant => attendant.departmentId === selectedDepartmentId
            )
        }

        setFilteredAttendants(filtered)
        setCurrentPage(1)
    }, [searchTerm, selectedDepartmentId, attendants])

    useEffect(() => {
        const endIndex = currentPage * itemsPerPage
        setDisplayedAttendants(filteredAttendants.slice(0, endIndex))
    }, [filteredAttendants, currentPage, itemsPerPage])

    const loadAttendants = async () => {
        const organizationId =
            user?.organizationId ?? user?.company?.organizationId
        if (!organizationId) return

        setLoading(true)
        try {
            const response = await getAttendantsByOrganization(
                organizationId,
                isPartner ? user?.partnerId : undefined
            )
            if (response.success) {
                setAttendants(response.result)
                setFilteredAttendants(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar atendentes')
            }
        } catch (error) {
            toast.error('Erro ao carregar atendentes')
        } finally {
            setLoading(false)
        }
    }

    const loadDepartments = async () => {
        const organizationId =
            user?.organizationId ?? user?.company?.organizationId
        if (!organizationId) return

        try {
            const response = await getDepartmentsByOrganization(
                organizationId,
                isPartner ? user?.partnerId : undefined
            )
            if (response.success) {
                setDepartments(response.result)
            }
        } catch (error) {
            console.error('Erro ao carregar setores:', error)
        }
    }

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault()
    }

    const handleLoadMore = () => {
        setLoadingMore(true)
        setTimeout(() => {
            setCurrentPage(prev => prev + 1)
            setLoadingMore(false)
        }, 300)
    }

    const handleDelete = (attendantId: number) => {
        setAttendantToDelete(attendantId)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!attendantToDelete) return

        setDeleting(true)
        try {
            const response = await deleteAttendant(attendantToDelete)
            if (response.success) {
                toast.success('Atendente excluido com sucesso')
                loadAttendants()
                setShowDeleteModal(false)
                setAttendantToDelete(null)
            } else {
                toast.error(response.error || 'Erro ao excluir atendente')
            }
        } catch (error) {
            toast.error('Erro ao excluir atendente')
        } finally {
            setDeleting(false)
        }
    }

    const handleToggleOnHold = async (attendantId: number) => {
        try {
            const response = await toggleOnHold(attendantId)
            if (response.success) {
                const attendant = attendants.find(a => a.id === attendantId)
                const newStatus = !attendant?.onHold
                toast.success(
                    newStatus ? 'Atendente pausado' : 'Atendente ativado'
                )
                loadAttendants()
            } else {
                toast.error(
                    response.error || 'Erro ao alternar pausa do atendente'
                )
            }
        } catch (error) {
            toast.error('Erro ao alternar pausa do atendente')
        }
    }

    const hasMore = displayedAttendants.length < filteredAttendants.length

    const getDepartmentName = (departmentId: number) => {
        const department = departments.find(d => d.id === departmentId)
        return department?.name || 'Setor nao encontrado'
    }

    const getAvatarGradient = (index: number, onHold: boolean) => {
        if (onHold) return 'from-gray-400 to-gray-500'
        const gradients = [
            'from-violet-500 via-purple-500 to-fuchsia-500',
            'from-cyan-500 via-blue-500 to-indigo-500',
            'from-emerald-500 via-teal-500 to-cyan-500',
            'from-rose-500 via-pink-500 to-purple-500',
            'from-amber-500 via-orange-500 to-red-500',
            'from-blue-500 via-indigo-500 to-violet-500',
        ]
        return gradients[index % gradients.length]
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[500px] gap-6">
                <div className="relative">
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500 flex items-center justify-center animate-pulse shadow-2xl shadow-purple-500/30">
                        <BsRobot className="text-4xl text-white" />
                    </div>
                    <div className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-cyan-400 animate-ping"></div>
                    <div className="absolute -bottom-1 -left-1 w-4 h-4 rounded-full bg-fuchsia-400 animate-ping animation-delay-300"></div>
                </div>
                <div className="text-center">
                    <p className="text-base-content/70 font-medium">
                        Carregando agentes de IA...
                    </p>
                    <p className="text-xs text-base-content/50 mt-1">
                        Sincronizando redes neurais
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-6 p-4 md:p-6">
            {/* Hero Header com tema AI */}
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-base-100 via-purple-50/30 to-base-100 p-3 shadow-sm border border-base-300/50">
                <div className="relative z-10">
                    {/* Top Section */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500 flex items-center justify-center shadow-md">
                                <BsRobot className="text-xl text-white" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h1 className="text-lg font-bold text-base-content">
                                        Agentes de IA
                                    </h1>
                                    <span className="px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700 text-xs font-medium border border-purple-200">
                                        AI
                                    </span>
                                </div>
                            </div>
                        </div>

                        {canCreate && (
                            <div className="flex items-center gap-2">
                                <button
                                    className="btn btn-outline btn-secondary btn-sm gap-1.5"
                                    onClick={() => setShowImportModal(true)}
                                >
                                    <MdFileDownload className="text-base" />
                                    <span className="hidden sm:inline">
                                        Importar
                                    </span>
                                </button>
                                <button
                                    onClick={() =>
                                        navigate('/atendimento/agents/new')
                                    }
                                    className="btn btn-secondary btn-sm gap-1.5"
                                >
                                    <MdAdd className="text-base" />
                                    <span>Criar Agente</span>
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Stats Row */}
                    <div className="grid grid-cols-3 gap-2 mt-3">
                        <div className="bg-base-200/50 rounded-lg p-2 border border-base-300/50">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                                    <BsCpu className="text-xs text-white" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-base-content">
                                        {attendants.length}
                                    </p>
                                    <p className="text-[10px] text-base-content/60">
                                        Total
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-base-200/50 rounded-lg p-2 border border-base-300/50">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded bg-gradient-to-br from-emerald-500 to-cyan-600 flex items-center justify-center relative">
                                    <BsLightning className="text-xs text-white" />
                                    <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-emerald-400 rounded-full"></span>
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-base-content">
                                        {activeCount}
                                    </p>
                                    <p className="text-[10px] text-base-content/60">
                                        Ativos
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="bg-base-200/50 rounded-lg p-2 border border-base-300/50">
                            <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                                    <MdPause className="text-xs text-white" />
                                </div>
                                <div>
                                    <p className="text-sm font-bold text-base-content">
                                        {pausedCount}
                                    </p>
                                    <p className="text-[10px] text-base-content/60">
                                        Pausados
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <form onSubmit={handleSearch} className="flex-1 relative">
                    <MdSearch className="absolute z-10 left-4 top-1/2 -translate-y-1/2 text-base-content/40 text-xl" />
                    <input
                        type="text"
                        placeholder="Buscar agentes por nome ou regras..."
                        className="input input-bordered w-full pl-12 bg-base-100"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            onClick={() => setSearchTerm('')}
                            className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-xs btn-circle"
                        >
                            <MdClose className="text-base" />
                        </button>
                    )}
                </form>
                <select
                    className="select select-bordered w-full sm:w-56 bg-base-100"
                    value={selectedDepartmentId}
                    onChange={e =>
                        setSelectedDepartmentId(
                            e.target.value ? Number(e.target.value) : ''
                        )
                    }
                >
                    <option value="">Todos os setores</option>
                    {departments.map(dept => (
                        <option key={dept.id} value={dept.id}>
                            {dept.name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Attendants List */}
            {filteredAttendants.length === 0 ? (
                <div className="rounded-2xl bg-base-100 border border-base-300/50 p-8 md:p-12">
                    <div className="flex flex-col items-center justify-center text-center">
                        <div className="relative mb-6">
                            <div className="w-24 h-24 rounded-2xl bg-base-200 flex items-center justify-center border border-base-300">
                                <BsRobot className="text-5xl text-base-content/30" />
                            </div>
                            <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-xl bg-secondary/10 flex items-center justify-center border border-secondary/20">
                                <HiSparkles className="text-xl text-secondary/60" />
                            </div>
                        </div>

                        <h3 className="text-xl font-bold text-base-content mb-2">
                            {searchTerm || selectedDepartmentId
                                ? 'Nenhum agente encontrado'
                                : 'Crie seu primeiro Agente de IA'}
                        </h3>
                        <p className="text-base-content/60 text-sm max-w-md mb-6">
                            {searchTerm || selectedDepartmentId
                                ? 'Tente ajustar sua busca ou filtros'
                                : 'Agentes de IA atendem seus clientes 24 horas por dia com respostas inteligentes e personalizadas'}
                        </p>

                        {!searchTerm && !selectedDepartmentId && canCreate && (
                            <button
                                onClick={() =>
                                    navigate('/atendimento/agents/new')
                                }
                                className="btn btn-secondary gap-2"
                            >
                                <MdAdd className="text-xl" />
                                Criar Primeiro Agente
                            </button>
                        )}
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {displayedAttendants.map((attendant, index) => (
                        <div
                            key={attendant.id}
                            className={`group relative overflow-hidden rounded-2xl bg-base-100 border border-base-300/50 hover:border-violet-500/30 hover:shadow-xl hover:shadow-violet-500/10 transition-all duration-300 ${attendant.onHold ? 'opacity-70' : ''}`}
                        >
                            {/* Top gradient bar */}
                            <div
                                className={`h-1 bg-gradient-to-r ${getAvatarGradient(index, attendant.onHold)}`}
                            ></div>

                            <div className="p-5">
                                {/* Header */}
                                <div className="flex items-start gap-4 mb-4">
                                    <div className="relative">
                                        <div
                                            className={`w-14 h-14 rounded-xl bg-gradient-to-br ${getAvatarGradient(index, attendant.onHold)} flex items-center justify-center shadow-lg ${!attendant.onHold ? 'shadow-purple-500/20' : ''}`}
                                        >
                                            <BsRobot className="text-2xl text-white" />
                                        </div>
                                        {!attendant.onHold && (
                                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-emerald-400 rounded-full border-2 border-base-100 flex items-center justify-center">
                                                <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                                            </span>
                                        )}
                                        {attendant.onHold && (
                                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full border-2 border-base-100 flex items-center justify-center">
                                                <MdPause className="text-[8px] text-amber-900" />
                                            </span>
                                        )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-base font-bold text-base-content truncate mb-1">
                                            {attendant.name}
                                        </h3>
                                        <div className="flex items-center gap-1.5">
                                            <MdBusiness className="text-sm text-base-content/40" />
                                            <span className="text-xs text-base-content/60 truncate">
                                                {getDepartmentName(
                                                    attendant.departmentId
                                                )}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Status Badge */}
                                    <div
                                        className={`px-2 py-1 rounded-lg text-xs font-medium ${
                                            attendant.onHold
                                                ? 'bg-amber-100 text-amber-700'
                                                : 'bg-emerald-100 text-emerald-700'
                                        }`}
                                    >
                                        {attendant.onHold ? 'Pausado' : 'Ativo'}
                                    </div>
                                </div>

                                {/* Rules Preview */}
                                {attendant.rules && (
                                    <div className="bg-base-200/50 rounded-xl p-3 mb-4">
                                        <div className="flex items-center gap-1.5 mb-1.5">
                                            <MdPsychology className="text-sm text-violet-500" />
                                            <span className="text-xs font-medium text-base-content/70">
                                                Instrucoes
                                            </span>
                                        </div>
                                        <p className="text-sm text-base-content/60 line-clamp-2">
                                            {attendant.rules}
                                        </p>
                                    </div>
                                )}

                                {/* Footer */}
                                <div className="flex items-center justify-between pt-3 border-t border-base-300/50">
                                    <span className="text-xs text-base-content/40">
                                        Criado em{' '}
                                        {new Date(
                                            attendant.createdAt
                                        ).toLocaleDateString('pt-BR')}
                                    </span>

                                    <div className="flex items-center gap-1">
                                        <button
                                            onClick={() =>
                                                handleToggleOnHold(attendant.id)
                                            }
                                            className={`btn btn-sm btn-ghost ${attendant.onHold ? 'text-emerald-500 hover:bg-emerald-500/10' : 'text-amber-500 hover:bg-amber-500/10'}`}
                                            title={
                                                attendant.onHold
                                                    ? 'Ativar agente'
                                                    : 'Pausar agente'
                                            }
                                        >
                                            {attendant.onHold ? (
                                                <MdPlayArrow className="text-lg" />
                                            ) : (
                                                <MdPause className="text-lg" />
                                            )}
                                        </button>
                                        {canUpdate && (
                                            <button
                                                onClick={() =>
                                                    navigate(
                                                        `/atendimento/agents/edit/${attendant.id}`
                                                    )
                                                }
                                                className="btn btn-sm btn-ghost text-base-content/60 hover:text-violet-500 hover:bg-violet-500/10"
                                                title="Editar"
                                            >
                                                <MdEdit className="text-lg" />
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button
                                                onClick={() =>
                                                    handleDelete(attendant.id)
                                                }
                                                className="btn btn-sm btn-ghost text-base-content/60 hover:text-error hover:bg-error/10"
                                                title="Excluir"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Load More */}
            {hasMore && (
                <div className="flex justify-center pt-4">
                    <button
                        onClick={handleLoadMore}
                        className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-base-200 hover:bg-base-300 text-base-content font-medium transition-all"
                        disabled={loadingMore}
                    >
                        {loadingMore ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Carregando...
                            </>
                        ) : (
                            <>
                                Ver mais agentes
                                <span className="text-xs opacity-60">
                                    (+
                                    {Math.min(
                                        itemsPerPage,
                                        filteredAttendants.length -
                                            displayedAttendants.length
                                    )}
                                    )
                                </span>
                            </>
                        )}
                    </button>
                </div>
            )}

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setAttendantToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Excluir Agente de IA"
                message="Tem certeza de que deseja excluir este agente? Esta acao nao pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={deleting}
            />

            <ImportDataModal
                isOpen={showImportModal}
                onClose={() => setShowImportModal(false)}
                module={ShareableModule.ATTENDANT}
                moduleLabel="Atendentes"
                fetchItems={async (scopeId: number) => {
                    const response = await getAttendantsByOrganization(scopeId)
                    return response.success ? response.result : []
                }}
                renderItem={(item: Attendant) => (
                    <div>
                        <p className="text-sm font-medium truncate">
                            {item.name}
                        </p>
                        {item.rules && (
                            <p className="text-xs text-base-content/60 truncate">
                                {item.rules.substring(0, 80)}
                                {item.rules.length > 80 ? '...' : ''}
                            </p>
                        )}
                    </div>
                )}
                getItemId={(item: Attendant) => item.id}
                getItemName={(item: Attendant) => item.name}
                onImportComplete={loadAttendants}
            />
        </div>
    )
}
